import React from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { Add as AddIcon, AspectRatio as AspectRatioIcon } from "@mui/icons-material";
//components
import CardTable from "components/CardTable/CardTable";
import Snackbar from "components/Snackbar/Snackbar";
import Button from "components/CustomButtons/Button.js";
import { Box, TextField } from "@mui/material";
import { configTable } from "./config";
import { ModalV2 } from "components/Modal/ModalV2";
import NewExpense from "./NewExpense";
import { TableInputs } from "components/CardTable/TableInputs";
//actions
import {
  getExpenses,
  closeSnackbar,
  setErrorSnackbar,
  setConfigFilter,
  setConfigPage,
  setConfigPageSize,
  setConfigSorted,
  setExpensesFilters,
} from "redux/ducks/expenses";
import { useSearch } from "hooks/useSearch";
import { formatNumber } from "helpers/getNumberFormats";
import { expenseSelector } from "./selector";

function Expenses() {
  const dispatch = useDispatch();
  const { isLoadingComp } = useSelector(({ search }) => search);
  const { buildCompLoadings } = useSearch();
  const [showNewExpense, setShowNewExpense] = React.useState(false);

  //selectors
  const {
    station,
    error,
    errorMessage,
    colorError,
    startDate,
    endDate,
  } = useSelector((state) => state.expenses);
  const { filtered, page, pageSize, sorted } = useSelector(
    (state) => state.expenses.config
  );
  const expensesSel = useSelector((state) => expenseSelector(state));
  const [totals, setTotals] = React.useState({
    stationTotal: '0.00',
    expenseTotal: '0.00'
  })
  const { stationTotal, expenseTotal } = totals
  //local state
  const handleOpenNewExpense = () => {
    setShowNewExpense(true);
  };

  const handleCloseNewExpense = () => {
    setShowNewExpense(false);
  };

  const throwSnackBar = (obj) => {
    dispatch(setErrorSnackbar(obj));
  };

  const clearSnackbar = () => {
    dispatch(closeSnackbar());
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    const currentDay = dayjs();

    if (!startDate) {
      throwSnackBar({
        colorError: "danger",
        errorMessage: "No se ha seleccionado fecha de inicio.",
      });
      return;
    }
    if (!endDate) {
      throwSnackBar({
        colorError: "danger",
        errorMessage: "No se ha seleccionado fecha final.",
      });
      return;
    }

    if (!dayjs(startDate).isSameOrBefore(dayjs(endDate), "day")) {
      throwSnackBar({
        colorError: "danger",
        errorMessage: "La fecha final no puede ser menor a la inicial.",
      });
      return;
    }

    if (dayjs(endDate).diff(dayjs(currentDay), "day") >= 1) {
      throwSnackBar({
        colorError: "danger",
        errorMessage:
          "La fecha final no puede ser mayor a un dia del dia actual",
      });
      return;
    }
    if (!station || (Array.isArray(station) && station.length === 0)) {
      throwSnackBar({
        colorError: "danger",
        errorMessage: "No hay planta seleccionada",
      });
      return;
    }
    const stationsPayload = Array.isArray(station) ? station : [station];
    dispatch(getExpenses(stationsPayload, startDate, endDate, dispatch));
  };


  const handleChangeFilteredMemoized = React.useCallback(
    (event) => {
      dispatch(setConfigFilter(event));
    },
    [dispatch]
  );
  const handleChangePageMemoized = React.useCallback(
    (event) => {
      dispatch(setConfigPage(event));
    },
    [dispatch]
  );
  const handleChangePageSizeMemoized = React.useCallback(
    (event) => {
      dispatch(setConfigPageSize(event));
    },
    [dispatch]
  );
  const handleChangeSortedMemoized = React.useCallback(
    (event) => {
      dispatch(setConfigSorted(event));
    },
    [dispatch]
  );

  React.useEffect(() => buildCompLoadings(["loadingExpenses"]), []);
  const ref = React.useRef(null)

  React.useEffect(() => {
    if (ref?.current) {
      const filter = ref.current.getResolvedState().sortedData
      const sum = filter.reduce((acc, curr) => {
        acc = {
          stationTotal: acc.stationTotal + curr.amountByStationC,
          expenseTotal: acc.expenseTotal + curr.amountC
        }
        return acc
      }, {
        stationTotal: 0,
        expenseTotal: 0
      })
      setTotals({
        stationTotal: formatNumber(sum.stationTotal),
        expenseTotal: formatNumber(sum.expenseTotal)
      })
    }
  }, [filtered, page, pageSize, sorted, expensesSel])
  return (
    <>
      <CardTable
        reference={ref}
        hasHeader
        data={expensesSel}
        columns={configTable}
        icon={<AspectRatioIcon />}
        title="Registro de Gastos"
        previousText="Anterior"
        nextText="Siguiente"
        noDataText="Sin datos"
        pageText="Pagina"
        ofText="de"
        blockBtn
        isLoading={isLoadingComp?.loadingExpenses}
        filtered={filtered}
        page={page}
        sorted={sorted}
        pageSize={pageSize}
        filterable
        onPageChange={handleChangePageMemoized}
        onPageSizeChange={handleChangePageSizeMemoized}
        onSortedChange={handleChangeSortedMemoized}
        onFilteredChange={handleChangeFilteredMemoized}
        tableContent={
          <TableInputs
            reducer="expenses"
            dates
            stations
            multiStations
            dispatchAction={setExpensesFilters}
            submitAction={handleSubmit}
            loading={isLoadingComp?.loadingExpenses}
          />
        }
        hasControl
        controls={
          <Box style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 16
          }}>
            <TextField
              id="total-station-id"
              label="Importe planta total"
              variant="standard"
              value={`$ ${stationTotal}`}
            />
            <TextField
              id="total-expense-id"
              label="Importe gasto total"
              variant="standard"
              value={`$ ${expenseTotal}`}
            />
            <Button
              justIcon
              round
              color="info"
              onClick={handleOpenNewExpense}
            >
              <AddIcon sx={{margin: "0 !Important"}} />
            </Button>
          </Box>
        }
      />
      <Snackbar
        place="tr"
        color={colorError}
        message={errorMessage}
        open={error}
        closeNotification={() => clearSnackbar()}
        close
      />
      <ModalV2
        open={showNewExpense}
        onClose={handleCloseNewExpense}
        toRight
        halfScreen
        title="Registro de Gastos"
      >
        {showNewExpense && <NewExpense handleClose={handleCloseNewExpense} />}
      </ModalV2>
    </>
  );
}

export default Expenses;
